import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Ein Eintrag der Auswahlliste. */
export interface ComboboxOption {
  id: string;
  /** Was in der Zeile und nach der Auswahl im Feld steht. */
  label: string;
  /** Rechts daneben, gedaempft: Kundennummer, Ticketnummer, Kunde. */
  meta?: string;
  /** Unter dem Namen, klein: "inaktiv", eine E-Mail-Adresse. */
  hint?: string;
}

interface ComboboxProps {
  /** Kennung des Eingabefelds -- fuer ``<label htmlFor>``. */
  id?: string;
  /** Beschriftung, wenn kein sichtbares ``<label>`` daneben steht. */
  ariaLabel?: string;
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
  options: ComboboxOption[];
  /**
   * Suche beim Server. Ist sie gesetzt, filtert das Feld **nicht** selbst:
   * Die Liste, die hereinkommt, ist schon das Ergebnis der Eingabe.
   */
  onSearch?: (term: string) => void;
  /**
   * Jeder getippte Text, ungekuerzt -- und nur bei Eingabe, nicht beim
   * Oeffnen. Fuer Felder mit freier Eingabe, deren Wert schon beim Tippen
   * gelten soll: Wer eine Adresse eintippt und dann "Anlegen" klickt, hat
   * sie gemeint, auch ohne Eingabetaste.
   */
  onInput?: (text: string) => void;
  loading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Darf die Auswahl wieder geleert werden? Vorgabe: ja. */
  clearable?: boolean;
  /**
   * Freie Eingabe zulassen -- die Eingabetaste uebernimmt dann auch einen
   * Text, zu dem es keinen Eintrag gibt (``id`` ist dabei leer). Fuer
   * E-Mail-Adressen, die nicht im CRM stehen.
   */
  allowFreeText?: boolean;
  /** Zusaetzliche Klasse am aeusseren Element -- etwa fuer eine schmale Tabellenzelle. */
  className?: string;
  style?: React.CSSProperties;
  /** Ersetzt die Gestaltung des Eingabefelds (Vorgabe ``field__control``). */
  inputClassName?: string;
}

/**
 * Ein Auswahlfeld, in das man tippen kann -- die Autovervollstaendigung.
 *
 * **Es verhaelt sich zuerst wie eine Aufklappliste.** Ein Klick oeffnet die
 * Liste, ein Klick auf einen Eintrag waehlt ihn. Wer nicht tippen will, merkt
 * keinen Unterschied. Wer tippt, grenzt die Liste ein -- "mus" laesst "Muster
 * GmbH" stehen, gleich wo im Namen die Buchstaben stehen.
 *
 * **Zwei Quellen, ein Verhalten.** Ohne ``onSearch`` filtert das Feld die
 * uebergebene Liste selbst: fuer Bestaende, die ohnehin geladen sind. Mit
 * ``onSearch`` fragt der Aufrufer den Server, und die Liste ist nie bei der
 * ersten Seite abgeschnitten -- genau daran scheiterte vorher die
 * Kundenauswahl beim Ticketanlegen, die nach 25 Kunden aufhoerte.
 *
 * **Bedienung nach dem ARIA-Muster "Combobox".** Pfeiltasten waehlen, die
 * Eingabetaste uebernimmt, Esc schliesst. Hervorgehoben ist, was die Tastatur
 * gewaehlt hat; die Maus setzt die Hervorhebung, statt eine zweite zu fuehren.
 * Das Muster stammt aus der Befehlspalette, die es seit F1-43 so bedient.
 *
 * **Die Liste oeffnet beim Klick, beim Tippen und mit Pfeil nach unten --
 * nicht schon beim Fokus.** Wer mit der Tabulatortaste durch ein Formular
 * geht, soll nicht bei jedem Feld eine Liste aufklappen sehen.
 *
 * **Waehrend der Eingabe steht der Suchbegriff im Feld, sonst die Auswahl.**
 * Verlaesst man das Feld ohne zu waehlen, bleibt die vorige Auswahl stehen --
 * eine halb getippte Suche ist keine Entscheidung.
 */
export function Combobox({
  id,
  ariaLabel,
  value,
  onChange,
  options,
  onSearch,
  onInput,
  loading = false,
  placeholder,
  disabled = false,
  clearable = true,
  allowFreeText = false,
  className,
  style,
  inputClassName = 'field__control',
}: ComboboxProps) {
  const { t } = useTranslation();
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const listId = `${inputId}-list`;

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const needle = term.trim().toLowerCase();
  const visible =
    onSearch || needle === ''
      ? options
      : options.filter(
          (option) =>
            option.label.toLowerCase().includes(needle) ||
            (option.meta?.toLowerCase().includes(needle) ?? false) ||
            (option.hint?.toLowerCase().includes(needle) ?? false),
        );

  // Die Hervorhebung darf nicht hinter das Ende rutschen, wenn ein
  // Suchergebnis kuerzer ist als das vorige.
  useEffect(() => {
    setActive((current) => (current >= visible.length ? 0 : current));
  }, [visible.length]);

  // Die hervorgehobene Zeile bleibt im sichtbaren Ausschnitt der Liste.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${String(active)}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  // Esc schliesst die Liste und **nur** die Liste. Steht das Feld in einem
  // Dialog, hoert der Dialog am Dokument mit -- und zwar in der Fangphase,
  // also vor jedem Handler am Feld. Deshalb faengt das Feld die Taste noch
  // eine Stufe frueher, am Fenster, solange seine Liste offen ist.
  useEffect(() => {
    if (!open) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.activeElement !== inputRef.current) return;
      event.stopPropagation();
      event.preventDefault();
      setOpen(false);
      setTerm('');
    };
    window.addEventListener('keydown', onEscape, true);
    return () => {
      window.removeEventListener('keydown', onEscape, true);
    };
  }, [open]);

  const openList = () => {
    if (disabled || open) return;
    // Bei freier Eingabe steht der bisherige Text zum Weiterschreiben im Feld;
    // eine Auswahl dagegen beginnt die Suche leer, sonst saehe man nur den
    // einen Eintrag, der schon gewaehlt ist.
    const start = allowFreeText && value ? value.label : '';
    setOpen(true);
    setTerm(start);
    setActive(0);
    onSearch?.(start);
  };

  const close = () => {
    setOpen(false);
    setTerm('');
  };

  const choose = (option: ComboboxOption) => {
    onChange(option);
    close();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) {
          openList();
          return;
        }
        setActive((current) => (visible.length === 0 ? 0 : (current + 1) % visible.length));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((current) =>
          visible.length === 0 ? 0 : (current - 1 + visible.length) % visible.length,
        );
        break;
      case 'Enter': {
        if (!open) return;
        // Im Formular soll die Eingabetaste waehlen und nicht abschicken.
        event.preventDefault();
        const option = visible[active];
        if (option) {
          choose(option);
        } else if (allowFreeText && term.trim() !== '') {
          choose({ id: '', label: term.trim() });
        }
        break;
      }
      case 'Tab':
        close();
        break;
    }
  };

  const activeOption = open ? visible[active] : undefined;

  return (
    <div className={className ? `combobox ${className}` : 'combobox'} style={style}>
      <div className="combobox__control">
        <input
          ref={inputRef}
          id={inputId}
          className={`${inputClassName} combobox__input`}
          type="text"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeOption ? `${listId}-${activeOption.id}` : undefined}
          autoComplete="off"
          disabled={disabled}
          value={open ? term : (value?.label ?? '')}
          placeholder={open && value ? value.label : placeholder}
          onClick={openList}
          onBlur={close}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            if (!open) setOpen(true);
            setTerm(event.target.value);
            setActive(0);
            onSearch?.(event.target.value.trim());
            onInput?.(event.target.value);
          }}
        />
        {loading && open ? <span className="spinner combobox__spinner" aria-hidden="true" /> : null}
        {clearable && value && value.id !== '' && !disabled ? (
          <button
            type="button"
            className="combobox__clear"
            aria-label={t('combobox.clear')}
            // Vor dem Verlust des Fokus, sonst schliesst das Feld zuerst und
            // der Klick trifft ins Leere.
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              onChange(null);
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {open ? (
        <ul className="combobox__list" id={listId} role="listbox" ref={listRef}>
          {visible.length === 0 ? (
            <li className="combobox__empty">
              {loading
                ? t('app.loading')
                : allowFreeText && term.trim() !== ''
                  ? t('combobox.useFreeText', { value: term.trim() })
                  : t('combobox.noResults')}
            </li>
          ) : null}
          {visible.map((option, index) => (
            <li
              key={option.id}
              id={`${listId}-${option.id}`}
              role="option"
              aria-selected={index === active}
              data-index={index}
              data-active={index === active}
              data-chosen={value?.id === option.id}
              className="combobox__option"
              // ``mousedown`` und nicht ``click``: Beim Klick hat das Feld den
              // Fokus schon verloren und die Liste ist zu.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option);
              }}
              onMouseMove={() => {
                setActive(index);
              }}
            >
              <span className="combobox__label">
                {option.label}
                {option.hint ? <span className="combobox__hint">{option.hint}</span> : null}
              </span>
              {option.meta ? <span className="combobox__meta">{option.meta}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
